/**
 * Capacitor App Update Dialog Component
 *
 * Shows update information and prompts user to update via Play Store.
 * Also doubles as the app-version details modal for manual checks from Header.
 *
 * @author TotalAccess Development Team
 * @date November 27, 2025
 */

'use client';

import React from 'react';
import { createPortal } from 'react-dom';
import { Download, X, AlertCircle, ExternalLink, Smartphone } from 'lucide-react';
import type { UpdateCheckResult, UpdateProgress } from '@/lib/capacitorOTAUpdateService';
import { useTheme } from '@/contexts/ThemeContext';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

export interface CapacitorOTAUpdateDialogProps {
  isOpen: boolean;
  updateInfo: UpdateCheckResult | null;
  progress: UpdateProgress | null;
  currentVersion?: string;
  isChecking?: boolean;
  onDownload: () => void;  // Opens the appropriate app store
  onSkip: () => void;
  onDismiss: () => void;
  onCheckAgain?: () => void;
}

function formatReleaseLabel(label?: string | null, rawValue?: string | null): string {
  if (label) {
    return label;
  }

  if (!rawValue) {
    return 'Unknown';
  }

  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown';
  }

  return parsed.toLocaleString();
}

export function CapacitorOTAUpdateDialog({
  isOpen,
  updateInfo,
  progress,
  currentVersion,
  isChecking = false,
  onDownload,
  onDismiss,
  onCheckAgain,
}: CapacitorOTAUpdateDialogProps) {
  const { primaryColor } = useTheme();

  const isVersionCheckInProgress = isChecking || progress?.stage === 'checking';

  if (!isOpen || (!updateInfo && !isVersionCheckInProgress)) {
    return null;
  }

  const isMandatory = updateInfo?.mandatory || false;
  const isRedirecting = progress?.stage === 'redirecting';
  const isCheckFailed = updateInfo?.checkFailed === true;
  const hasError = progress?.stage === 'error' || isCheckFailed;
  const hasUpdateAvailable = updateInfo?.updateAvailable === true;
  const isAppGallery = updateInfo?.storeChannel === 'appgallery';
  const storeName = isAppGallery ? 'AppGallery' : 'Play Store';
  const currentReleaseLabel = formatReleaseLabel(updateInfo?.currentReleasedAtLabel, updateInfo?.currentReleasedAt);
  const latestReleaseLabel = formatReleaseLabel(updateInfo?.latestReleasedAtLabel, updateInfo?.latestReleasedAt);
  const versionsBehind = typeof updateInfo?.versionsBehind === 'number' ? updateInfo.versionsBehind : null;
  const showVersionsBehind = versionsBehind !== null && versionsBehind > 0;
  const resolvedCurrentVersion = updateInfo?.currentVersion || currentVersion || 'Unknown';
  const resolvedLatestVersion = updateInfo?.latestVersion || resolvedCurrentVersion;
  const dialogTitle = isVersionCheckInProgress
    ? 'Checking App Version'
    : isCheckFailed
      ? 'Version Check Failed'
      : hasUpdateAvailable
        ? (isMandatory ? 'Required Update' : 'Update Available')
        : 'App Version Details';
  const statusSummary = isVersionCheckInProgress
    ? progress?.message || 'Comparing the installed build with the latest deployment record.'
    : isCheckFailed
      ? (updateInfo?.message || 'Could not verify the latest release right now.')
      : hasUpdateAvailable
        ? (versionsBehind && versionsBehind > 0
          ? `${versionsBehind} release${versionsBehind === 1 ? '' : 's'} behind`
          : `A newer build is ready in ${storeName}`)
        : 'This app is already on the latest known release.';

  const dialogContent = (
    <div className="fixed inset-0 z-[140] bg-gray-50 dark:bg-gray-900 overflow-y-auto" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className="px-2 pb-1 pt-2 sm:px-4 sm:pb-3 sm:pt-4">
        {/* ----- Header ----- */}
        <div className="mb-3 sm:mb-6 px-2 relative">
          <div className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg sm:mb-3 sm:h-16 sm:w-16">
              <Smartphone className="h-6 w-6 sm:h-8 sm:w-8" />
            </div>
            <h1 className="pm-heading-1 text-lg sm:text-2xl mb-1">{dialogTitle}</h1>
          </div>
          <button
            onClick={onDismiss}
            className="absolute right-2 top-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <main className="grid grid-cols-1 gap-2 px-2 md:grid-cols-2 sm:gap-4">
          {isVersionCheckInProgress ? (
            <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
              <BrandedLoadingSpinner
                size="md"
                message="Checking app version"
                subMessage={statusSummary}
                primaryColor={primaryColor}
                progress={typeof progress?.progress === 'number' ? progress.progress : 50}
                showSurface={false}
                showProgressBar
              />
            </div>
          ) : (
            <>
              <div className={`bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2 ${hasUpdateAvailable
                ? 'border-emerald-300 dark:border-emerald-700'
                : ''}`}>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{statusSummary}</p>
                {updateInfo?.registryOutOfSync && (
                  <p className="mt-2 text-xs text-slate-600 dark:text-slate-300">
                    Source: {updateInfo.dataSource === 'merged'
                      ? 'Merged local + canonical deployment history'
                      : updateInfo.dataSource === 'canonical'
                        ? 'Canonical deployment history'
                        : 'Local deployment history'}
                  </p>
                )}
              </div>

              <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Installed</p>
                <p className="mt-1 text-base font-semibold text-slate-900 dark:text-white">{resolvedCurrentVersion}</p>
                <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">Released {currentReleaseLabel}</p>
              </div>

              <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Latest</p>
                <p className="mt-1 text-base font-semibold text-emerald-700 dark:text-emerald-300">{resolvedLatestVersion}</p>
                <p className="mt-1 text-xs text-emerald-700/80 dark:text-emerald-300/80">Released {latestReleaseLabel}</p>
              </div>

              {showVersionsBehind && (
                <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    {versionsBehind} release{versionsBehind === 1 ? '' : 's'} behind the latest version.
                  </p>
                </div>
              )}

              {isMandatory && (
                <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                    <div className="text-sm text-amber-800 dark:text-amber-200">
                      <p className="mb-1 font-semibold">Mandatory Update</p>
                      <p>This update is required to continue using the app.</p>
                    </div>
                  </div>
                </div>
              )}

              {updateInfo?.releaseNotes && (
                <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
                  <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
                    What's New:
                  </h3>
                  <div className="max-h-32 overflow-y-auto rounded-lg bg-gray-50 p-3 text-sm text-gray-600 dark:bg-gray-900/50 dark:text-gray-400">
                    {updateInfo.releaseNotes}
                  </div>
                </div>
              )}

              {updateInfo?.changelog && updateInfo.changelog.length > 0 && (
                <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
                  <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
                    Changes:
                  </h3>
                  <ul className="max-h-32 space-y-1 overflow-y-auto text-sm text-gray-600 dark:text-gray-400">
                    {updateInfo.changelog.map((item, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="mt-1 text-blue-500">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {hasUpdateAvailable && (
                <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
                  <div className="flex items-start gap-2">
                    <Download className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                    <div className="text-sm text-blue-800 dark:text-blue-200">
                      <p className="mb-1 font-semibold">Update via {storeName}</p>
                      <p>You'll be redirected to {storeName} to download the latest version.</p>
                    </div>
                  </div>
                </div>
              )}

            </>
          )}

          {hasError && (
            <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
                <div className="text-sm text-red-800 dark:text-red-200">
                  <p className="mb-1 font-semibold">{isCheckFailed ? 'Could Not Verify Version' : `Could Not Open ${storeName}`}</p>
                  <p className="mb-2">{progress?.error || updateInfo?.message || `Please open ${storeName} manually.`}</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              {isRedirecting ? (
                <>
                  <div className="flex-1 py-2 text-center text-sm text-gray-600 dark:text-gray-400">
                    Opening {storeName}...
                  </div>
                  <button
                    onClick={onDismiss}
                    className="rounded-lg bg-gray-200 px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </>
              ) : isVersionCheckInProgress ? (
                <button
                  onClick={onDismiss}
                  className="flex-1 rounded-lg bg-gray-200 px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  Close
                </button>
              ) : (
                <>
                  {hasUpdateAvailable ? (
                    <button
                      onClick={onDownload}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 font-medium text-white transition-all shadow-lg"
                      style={{ background: `linear-gradient(to right, ${primaryColor}, ${primaryColor}dd)`, boxShadow: `0 10px 15px -3px ${primaryColor}30` }}
                    >
                      <ExternalLink className="h-4 w-4" />
                      {isMandatory ? 'Update Now' : `Open ${storeName}`}
                    </button>
                  ) : onCheckAgain ? (
                    <button
                      onClick={onCheckAgain}
                      className="flex-1 rounded-lg px-4 py-2 font-medium text-white transition-all shadow-lg"
                      style={{ background: `linear-gradient(to right, ${primaryColor}, ${primaryColor}dd)`, boxShadow: `0 10px 15px -3px ${primaryColor}30` }}
                    >
                      Check Again
                    </button>
                  ) : null}

                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(dialogContent, document.body)
    : dialogContent;
}

export default CapacitorOTAUpdateDialog;
